import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { messagingService } from '../../../services/messagingService';
import { useAuth } from '../../../contexts/AuthContext';
import NewTeamGroupChatModal from '../../pcf-admin-dashboard/components/NewTeamGroupChatModal';


const TeamChat = ({ messages, onSendMessage, teamMembers, currentUser }) => {
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showStartChatModal, setShowStartChatModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [chatType, setChatType] = useState('user'); // 'user' or 'team'
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [showGroupChatModal, setShowGroupChatModal] = useState(false);
  const [showParticipantsMenu, setShowParticipantsMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageChannelRef = useRef(null);
  const conversationChannelRef = useRef(null);
  const participantChannelRef = useRef(null);

  const emojis = ['👍', '👎', '❤️', '😂', '😮', '😢', '😡', '🎉', '🚀', '💡'];

  // Get available tabs based on user role
  const getAvailableTabs = () => {
    const baseTabs = [
      { id: 'all', label: 'All', count: getUnreadConversationsCount('all') },
      { id: 'direct', label: 'Direct', count: getUnreadConversationsCount('direct') }
    ];

    // ALWAYS add Teams tab for team group chats - visible to all roles
    baseTabs?.push({ 
      id: 'teams', 
      label: 'Teams', 
      count: getUnreadConversationsCount('teams') 
    });

    return baseTabs;
  };

  // Get unread conversations count for each category
  const getUnreadConversationsCount = (category) => {
    if (!conversations || conversations?.length === 0) return 0;

    let filtered = conversations?.filter(conv => conv?.hasUnreadMessages);

    switch (category) {
      case 'all':
        // Count all conversations with unread messages
        return filtered?.length;
      
      case 'direct':
        // Count direct message conversations with unread messages
        return filtered?.filter(conv => conv?.conversationType === 'direct')?.length;
      
      case 'teams':
        // Count all team group conversations with unread messages (for ALL roles)
        return filtered?.filter(conv => conv?.conversationType === 'group')?.length;
      
      default:
        return 0;
    }
  };

  const getDirectConversationsCount = () => {
    return conversations?.filter(conv => conv?.conversationType === 'direct')?.length || 0;
  };

  const getTeamConversationsCount = () => {
    return conversations?.filter(conv => conv?.conversationType === 'group')?.length || 0;
  };

  // Filter conversations based on active tab
  const getFilteredConversations = () => {
    let filtered = conversations || [];

    // Apply tab filter
    switch (activeTab) {
      case 'direct':
        filtered = filtered?.filter(conv => conv?.conversationType === 'direct');
        break;
      case 'teams':
        // Show ALL team group chats for all roles (not just student's team)
        filtered = filtered?.filter(conv => conv?.conversationType === 'group');
        break;
      case 'all':
      default:
        // Show all conversations
        break;
    }

    // Apply search filter
    if (searchQuery?.trim()) {
      filtered = filtered?.filter(conv => 
        getConversationTitle(conv)?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    return filtered;
  };

  // Enhanced real-time subscription setup
  useEffect(() => {
    if (selectedConversation?.id) {
      // Clean up existing subscriptions
      if (messageChannelRef?.current) {
        messagingService?.unsubscribe(messageChannelRef?.current);
      }

      console.log(`Setting up real-time subscription for conversation: ${selectedConversation?.id}`);

      // Subscribe to messages for the selected conversation
      messageChannelRef.current = messagingService?.subscribeToMessages(
        selectedConversation?.id,
        (newMessage) => {
          console.log('Received message in TeamChat:', newMessage);

          if (newMessage?.eventType === 'UPDATE') {
            // Handle message status updates
            setConversationMessages(prev => 
              prev?.map(msg => 
                msg?.id === newMessage?.id 
                  ? { ...msg, messageStatus: newMessage?.messageStatus }
                  : msg
              )
            );
          } else {
            // Handle new messages
            setConversationMessages(prev => {
              // Avoid duplicates - check both ID and timestamp
              const exists = prev?.some(msg => 
                msg?.id === newMessage?.id || 
                (msg?.content === newMessage?.content && 
                 Math.abs(new Date(msg?.createdAt) - new Date(newMessage?.createdAt)) < 1000)
              );
              
              if (!exists) {
                console.log('Adding new message to conversation:', newMessage?.id);
                return [...prev, newMessage];
              } else {
                console.log('Duplicate message detected, skipping:', newMessage?.id);
                return prev;
              }
            });

            // Mark conversation as having unread messages if it's not from current user
            if (newMessage?.senderId !== user?.id) {
              console.log('Marking conversation as unread for new message from:', newMessage?.sender?.fullName);
              setConversations(prev => 
                prev?.map(conv => 
                  conv?.id === selectedConversation?.id 
                    ? { ...conv, hasUnreadMessages: true, latestMessage: {
                        content: newMessage?.content,
                        createdAt: newMessage?.createdAt,
                        senderId: newMessage?.senderId,
                        sender: newMessage?.sender
                      }}
                    : conv
                )
              );
            }
          }
        }
      );
    }

    return () => {
      if (messageChannelRef?.current) {
        messagingService?.unsubscribe(messageChannelRef?.current);
      }
    };
  }, [selectedConversation?.id, user?.id]);

  // Enhanced conversation updates subscription
  useEffect(() => {
    if (user) {
      // Clean up existing subscriptions
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
      if (participantChannelRef?.current) {
        messagingService?.unsubscribe(participantChannelRef?.current);
      }

      console.log('Setting up conversation and participant subscriptions');

      // Subscribe to conversation updates
      conversationChannelRef.current = messagingService?.subscribeToConversations(
        (payload) => {
          console.log('Conversation update received:', payload);
          // Refresh conversations when any conversation is updated
          loadConversations();
        }
      );

      // Subscribe to participant updates (for read receipts, etc.)
      participantChannelRef.current = messagingService?.subscribeToConversationParticipants(
        (payload) => {
          console.log('Participant update received:', payload);
          // Refresh conversations when participants are updated
          loadConversations();
        }
      );
    }

    return () => {
      if (conversationChannelRef?.current) {
        messagingService?.unsubscribe(conversationChannelRef?.current);
      }
      if (participantChannelRef?.current) {
        messagingService?.unsubscribe(participantChannelRef?.current);
      }
    };
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  useEffect(() => {
    if (showStartChatModal) {
      if (chatType === 'user') {
        loadUsers();
      } else if (chatType === 'team') {
        loadTeams();
      }
    }
  }, [showStartChatModal, chatType]);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // New useEffect to handle auto-opening conversations from Quick Actions
  useEffect(() => {
    if (location?.state?.autoOpenConversation && conversations?.length > 0) {
      const conversationToOpen = conversations?.find(
        conv => conv?.id === location?.state?.autoOpenConversation
      );
      
      if (conversationToOpen) {
        handleConversationSelect(conversationToOpen);
        
        // Show success message based on contact type
        const contactType = location?.state?.contactType;
        const contactName = location?.state?.contactName;
        
        if (contactType === 'mentor') {
          console.log(`Opened conversation with mentor: ${contactName}`);
        } else if (contactType === 'city_official') {
          console.log(`Opened conversation with city official: ${contactName}`);
        }
        
        // Clear navigation state
        window.history?.replaceState({}, document.title);
      }
    }
  }, [location?.state, conversations]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 8000); // Increased timeout for longer error messages
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      // getAllUsers now handles role-based filtering internally
      const users = await messagingService?.getAllUsers();
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
      showError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const teams = await messagingService?.getTeams();
      setAllTeams(teams);
    } catch (error) {
      console.error('Failed to load teams:', error);
      showError('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadConversations = async () => {
    setLoadingConversations(true);
    setConnectionStatus('connecting');
    try {
      console.log('Loading conversations for user:', user?.id);
      const data = await messagingService?.getConversations();
      console.log('Loaded conversations:', data?.length);
      setConversations(data);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setConnectionStatus('disconnected');
      showError('Failed to load conversations: ' + error?.message);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      console.log('Loading messages for conversation:', conversationId);
      const data = await messagingService?.getMessages(conversationId);
      console.log('Loaded messages:', data?.length);
      setConversationMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
      showError('Failed to load messages: ' + error?.message);
    }
  };

  const handleConversationSelect = async (conversation) => {
    console.log('Selecting conversation:', conversation?.id, conversation);
    setSelectedConversation(conversation);
    setError(''); // Clear any existing errors
    await loadConversationMessages(conversation?.id);
    
    // Mark conversation as read when user opens it
    try {
      await messagingService?.markConversationAsRead(conversation?.id);
      console.log('Marked conversation as read:', conversation?.id);
      // Update local state to remove unread indicator
      setConversations(prev => 
        prev?.map(conv => 
          conv?.id === conversation?.id 
            ? { ...conv, hasUnreadMessages: false, lastReadAt: new Date()?.toISOString() }
            : conv
        )
      );
    } catch (error) {
      console.error('Failed to mark conversation as read:', error);
    }
  };

  const handleStartChat = () => {
    setShowStartChatModal(true);
    setChatType('user'); // Default to user chat
    setSelectedUser('');
    setSelectedTeam('');
    setError('');
  };

  const handleMyTeamChat = async () => {
    try {
      setError('');
      console.log('Opening My Team Chat...');
      
      // Automatically find or create team group chat
      const { conversationId, created } = await messagingService?.getOrCreateTeamGroupChat();
      
      console.log(created ? 'Created new team chat:' : 'Found existing team chat:', conversationId);
      
      // Reload conversations to get the latest data
      await loadConversations();
      
      // Find and select the team conversation
      const teamConversation = conversations?.find(c => c?.id === conversationId);
      if (teamConversation) {
        handleConversationSelect(teamConversation);
      } else {
        // If not found in current list, reload and try again
        setTimeout(async () => {
          await loadConversations();
          const conv = conversations?.find(c => c?.id === conversationId);
          if (conv) {
            handleConversationSelect(conv);
          }
        }, 500);
      }
      
    } catch (error) {
      console.error('Failed to open team chat:', error);
      showError(error?.message || 'Failed to open team chat');
    }
  };

  const handleCreateUserConversation = async () => {
    if (!selectedUser) return;

    try {
      console.log('Creating direct conversation with user:', selectedUser);
      const { conversationId } = await messagingService?.createDirectConversation(selectedUser);
      console.log('Created conversation:', conversationId);
      
      await loadConversations();
      
      // Find and select the new conversation
      const newConversation = conversations?.find(c => c?.id === conversationId);
      if (newConversation) {
        handleConversationSelect(newConversation);
      }
      
      setShowStartChatModal(false);
      setSelectedUser('');
    } catch (error) {
      console.error('Failed to create conversation:', error);
      showError(error?.message || 'Failed to create conversation');
    }
  };

  const handleCreateTeamConversation = async () => {
    if (!selectedTeam) return;

    try {
      console.log('Creating team conversation for team:', selectedTeam);
      const selectedTeamData = allTeams?.find(t => t?.id === selectedTeam);
      
      // Get team members to add as participants
      const teamMembers = await messagingService?.getTeamMembers(selectedTeam);
      const title = `${selectedTeamData?.teamName} (${teamMembers?.length} members)`;
      
      const conversationId = await messagingService?.createTeamGroupChat(selectedTeam, title);
      console.log('Created team conversation:', conversationId);
      
      await loadConversations();
      
      // Find and select the new conversation
      const newConversation = conversations?.find(c => c?.id === conversationId);
      if (newConversation) {
        handleConversationSelect(newConversation);
      }
      
      setShowStartChatModal(false);
      setSelectedTeam('');
    } catch (error) {
      console.error('Failed to create team conversation:', error);
      showError(error?.message || 'Failed to create team conversation');
    }
  };

  const handleCreateGroupChat = () => {
    setShowGroupChatModal(true);
  };

  const handleGroupChatSuccess = async (conversationId) => {
    await loadConversations();
    
    // Find and select the new conversation
    const newConversation = conversations?.find(c => c?.id === conversationId);
    if (newConversation) {
      handleConversationSelect(newConversation);
    }
  };

  // Enhanced message sending with better error handling
  const handleSendMessage = async () => {
    if (newMessage?.trim() && selectedConversation && !sendingMessage) {
      setSendingMessage(true);
      setError('');

      console.log('Sending message:', {
        conversationId: selectedConversation?.id,
        content: newMessage?.trim(),
        participants: selectedConversation?.participants?.length
      });

      try {
        const newMsg = await messagingService?.sendMessage(
          selectedConversation?.id, 
          newMessage?.trim()
        );
        
        console.log('Message sent successfully:', newMsg?.id, newMsg?.deliveryInfo);
        
        // Add message to local state immediately for better UX
        setConversationMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev?.some(msg => msg?.id === newMsg?.id);
          if (!exists) {
            return [...prev, newMsg];
          }
          return prev;
        });
        
        setNewMessage('');
        setReplyingTo(null);
        
        // Show detailed delivery status to user
        if (newMsg?.deliveryInfo) {
          console.log('Message delivery info:', newMsg?.deliveryInfo);
          
          if (newMsg?.deliveryInfo?.note) {
            // Show info about SMS being disabled
            console.log(newMsg?.deliveryInfo?.note);
          }
          
          // Show success message for successful deliveries
          if (newMsg?.deliveryInfo?.recipients > 0) {
            console.log(`Message delivered to ${newMsg?.deliveryInfo?.recipients} recipient(s) via app`);
          }
        }
        
        // Refresh conversations to update last message
        setTimeout(() => {
          loadConversations();
        }, 500); // Small delay to ensure message is processed

      } catch (error) {
        console.error('Failed to send message:', error);
        
        // More specific error handling
        if (error?.message?.includes('No recipients')) {
          showError('No recipients found in this conversation. Please check if other participants are still active.');
        } else if (error?.message?.includes('Not authenticated')) {
          showError('Your session has expired. Please log in again.');
        } else if (error?.message?.includes('SMS delivery service unavailable')) {
          showError('Message saved but SMS notifications may be delayed. Recipients will see it in the app.');
        } else {
          showError(error?.message || 'Failed to send message. Please try again.');
        }
        
        // Show more detailed error information in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Detailed error:', error);
        }
      } finally {
        setSendingMessage(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file && selectedConversation) {
      // Handle file upload logic here
      console.log('File selected:', file?.name);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);

    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === yesterday?.toDateString()) {
      return 'Yesterday';
    } else {
      return date?.toLocaleDateString();
    }
  };

  const getConversationTitle = (conversation) => {
    if (conversation?.title) return conversation?.title;
    
    if (conversation?.conversationType === 'direct' && conversation?.participants) {
      console.log("Testttt1:", conversation)
      const otherParticipant = conversation?.participants?.find(p => p?.userId !== user?.id);
      console.log("Testttt2:", otherParticipant)
      return otherParticipant?.user?.fullName || 'Unknown User';
    }
    
    return 'Group Chat';
  };

  const getConversationAvatar = (conversation) => {
    if (conversation?.conversationType === 'group') {
      return 'G';
    }
    
    const otherParticipant = conversation?.participants?.find(p => p?.userId !== user?.id);
    const name = otherParticipant?.user?.fullName || 'U';
    return name?.split(' ')?.map(n => n?.[0])?.join('') || 'U';
  };

  const getLastMessagePreview = (conversation) => {
    if (!conversation?.latestMessage) {
      return 'No messages yet';
    }

    const { latestMessage } = conversation;
    const isOwnMessage = latestMessage?.senderId === user?.id;
    const senderName = isOwnMessage ? 'You' : latestMessage?.sender?.fullName?.split(' ')?.[0] || 'Someone';
    const preview = latestMessage?.content?.length > 30 
      ? `${latestMessage?.content?.substring(0, 30)}...`
      : latestMessage?.content;

    return `${senderName}: ${preview}`;
  };

  // Enhanced message status icon with more detailed information
  const getMessageStatusIcon = (message) => {
    if (message?.senderId !== user?.id) return null;

    switch (message?.messageStatus) {
      case 'sending':
        return <Icon name="Clock" size={12} className="ml-1 text-muted-foreground animate-pulse" title="Sending..." />;
      case 'sent': case'delivered':
        return <Icon name="Check" size={12} className="ml-1 text-green-600" title="Delivered" />;
      case 'partial':
        return <Icon name="AlertTriangle" size={12} className="ml-1 text-yellow-600" title="Partially delivered" />;
      case 'failed':
        return <Icon name="AlertCircle" size={12} className="ml-1 text-destructive" title="Failed to deliver" />;
      default:
        return <Icon name="Check" size={12} className="ml-1" />;
    }
  };

  // Format participant role for display
  const formatParticipantRole = (participant) => {
    if (participant?.user?.role === 'student') {
      return participant?.user?.teamRole || 'Student';
    }
    
    const roleMap = {
      'city_official': 'City Official',
      'pcf_admin': 'PCF Admin',
      'student': 'Student'
    };
    
    return roleMap?.[participant?.user?.role] || 'User';
  };

  const filteredConversations = getFilteredConversations();
  const availableTabs = getAvailableTabs();
  const canCreateTeamChats = ['city_official', 'pcf_admin']?.includes(userProfile?.role);

  return (
    <div className="bg-card border border-border rounded-lg flex h-[600px] relative">
      {/* Enhanced Error Banner with more space and better formatting */}
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-destructive text-destructive-foreground p-3 text-sm z-50 flex items-start justify-between shadow-lg">
          <div className="flex items-start space-x-2 flex-1">
            <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
            <span className="flex-1 leading-relaxed">{error}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError('')}
            className="text-destructive-foreground hover:bg-destructive/20 flex-shrink-0 ml-2"
          >
            <Icon name="X" size={14} />
          </Button>
        </div>
      )}

      {/* Left Sidebar - Conversations */}
      <div className="w-1/3 border-r border-border flex flex-col">
        {/* Conversations Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">Conversations</h2>
              {/* Enhanced Connection Status Indicator */}
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'connecting'? 'bg-yellow-500 animate-pulse' : 'bg-red-500'
              }`} title={`Status: ${connectionStatus}`}></div>
            </div>
            <div className="flex items-center gap-2">
              {userProfile?.role === 'student' ? (
                <div className="relative group">
                  <Button 
                    variant="default" 
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Icon name="Plus" size={14} />
                    <Icon name="ChevronDown" size={12} />
                  </Button>
                  
                  {/* Dropdown Menu for Students */}
                  <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-lg shadow-elevated opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <div className="py-2">
                      <button
                        onClick={handleStartChat}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-3 transition-colors"
                      >
                        <Icon name="User" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">Direct Message</p>
                          <p className="text-xs text-muted-foreground">Chat with individual user</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={handleMyTeamChat}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-3 transition-colors"
                      >
                        <Icon name="Users" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">My Team Chat</p>
                          <p className="text-xs text-muted-foreground">Chat with my team members</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={handleCreateGroupChat}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-3 transition-colors"
                      >
                        <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-foreground">Custom Group</p>
                          <p className="text-xs text-muted-foreground">Create custom group chat</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={handleStartChat}
                  className="flex items-center gap-2"
                >
                  <Icon name="Plus" size={14} />
                  New
                </Button>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="relative mb-3">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Filter conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-muted rounded-lg p-1">
            {availableTabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{tab?.label}</span>
                  {tab?.count > 0 && (
                    <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                      activeTab === tab?.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted-foreground/20 text-muted-foreground'
                    }`}>
                      {tab?.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loadingConversations ? (
            <div className="p-4 space-y-4">
              {[...Array(3)]?.map((_, i) => (
                <div key={i} className="flex items-center space-x-3 animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredConversations?.length === 0 ? (
            <div className="p-4 text-center">
              <Icon name="MessageCircle" size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {activeTab === 'all' ? 'No conversations yet' : 
                 activeTab === 'direct' ? 'No direct messages' :
                 activeTab === 'teams' ? 'No team chats' : 'No conversations'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Start a new chat to get started</p>
            </div>
          ) : (
            filteredConversations?.map((conversation) => (
              <div
                key={conversation?.id}
                onClick={() => handleConversationSelect(conversation)}
                className={`flex items-center space-x-3 p-4 cursor-pointer hover:bg-muted transition-colors border-b border-border ${
                  selectedConversation?.id === conversation?.id ? 'bg-muted' : ''
                }`}
              >
                {/* Avatar */}
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0 relative">
                  <span className="text-sm font-medium text-primary-foreground">
                    {getConversationAvatar(conversation)}
                  </span>
                  {/* Enhanced Unread message indicator */}
                  {conversation?.hasUnreadMessages && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                  )}
                </div>

                {/* Conversation Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-medium text-foreground truncate">
                      {getConversationTitle(conversation)}
                    </h3>
                    {conversation?.latestMessage && (
                      <span className="text-xs text-muted-foreground">
                        {formatTime(conversation?.latestMessage?.createdAt)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <p className={`text-xs truncate ${
                      conversation?.hasUnreadMessages 
                        ? 'text-foreground font-medium' 
                        : 'text-muted-foreground'
                    }`}>
                      {getLastMessagePreview(conversation)}
                    </p>
                    
                    {/* Conversation type indicator */}
                    <div className="flex items-center gap-1 ml-2">
                      {conversation?.conversationType === 'group' && (
                        <Icon name="Users" size={12} className="text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Messages */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {getConversationAvatar(selectedConversation)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      {getConversationTitle(selectedConversation)}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {selectedConversation?.participants?.length} participant{selectedConversation?.participants?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 relative">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title="View Participants"
                    onClick={() => setShowParticipantsMenu(!showParticipantsMenu)}
                  >
                    <Icon name="MoreVertical" size={16} />
                  </Button>
                  
                  {/* Participants Menu */}
                  {showParticipantsMenu && (
                    <div className="absolute top-full right-0 mt-2 w-72 bg-popover border border-border rounded-lg shadow-elevated z-50">
                      <div className="p-3 border-b border-border">
                        <h4 className="text-sm font-semibold text-foreground">Participants</h4>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {selectedConversation?.participants?.map((participant) => (
                          <div 
                            key={participant?.userId}
                            className="p-3 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-xs font-medium text-primary-foreground">
                                  {participant?.user?.fullName?.split(' ')?.map(n => n?.[0])?.join('') || 'U'}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {participant?.user?.fullName || 'Unknown User'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  ({formatParticipantRole(participant)})
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 border-t border-border">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full"
                          onClick={() => setShowParticipantsMenu(false)}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/5">
              {conversationMessages?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="MessageCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No messages yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Start the conversation!</p>
                </div>
              ) : (
                conversationMessages?.map((message, index) => {
                  const isOwnMessage = message?.senderId === user?.id;
                  const showAvatar = !isOwnMessage && (
                    index === 0 || 
                    conversationMessages?.[index - 1]?.senderId !== message?.senderId
                  );

                  return (
                    <div key={`${message?.id}-${index}`} className={`flex items-end space-x-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      {!isOwnMessage && (
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary-foreground">
                            {message?.sender?.fullName?.split(' ')?.map(n => n?.[0])?.join('') || 'U'}
                          </span>
                        </div>
                      )}
                      
                      <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col`}>
                        {showAvatar && (
                          <p className="text-xs text-muted-foreground mb-1 px-2">
                            {message?.sender?.fullName || 'Unknown'}
                          </p>
                        )}
                        
                        <div
                          className={`px-3 py-2 rounded-2xl max-w-full break-words ${
                            isOwnMessage
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : 'bg-card border border-border rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm">{message?.content}</p>
                          <div className={`flex items-center justify-end mt-1 ${
                            isOwnMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}>
                            <span className="text-xs">{formatTime(message?.createdAt)}</span>
                            {getMessageStatusIcon(message)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Preview */}
            {replyingTo && (
              <div className="px-4 py-2 bg-muted border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Reply" size={14} className="text-primary" />
                    <span className="text-sm text-muted-foreground">
                      Replying to {replyingTo?.sender?.fullName}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setReplyingTo(null)}
                  >
                    <Icon name="X" size={14} />
                  </Button>
                </div>
                <p className="text-sm text-foreground truncate mt-1">
                  {replyingTo?.content}
                </p>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t border-border">
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder={sendingMessage ? "Sending..." : "Type a message..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sendingMessage}
                    className="rounded-full"
                  />
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={sendingMessage}
                  >
                    <Icon name="Paperclip" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    disabled={sendingMessage}
                  >
                    <Icon name="Smile" size={16} />
                  </Button>
                  <Button
                    variant="default"
                    size="icon"
                    onClick={handleSendMessage}
                    disabled={!newMessage?.trim() || sendingMessage}
                    className="rounded-full"
                  >
                    {sendingMessage ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Send" size={16} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="mt-2 p-2 bg-popover border border-border rounded-lg shadow-elevated">
                  <div className="flex flex-wrap gap-1">
                    {emojis?.map((emoji, index) => (
                      <button
                        key={index}
                        className="p-2 hover:bg-muted rounded transition-colors"
                        onClick={() => {
                          setNewMessage(prev => prev + emoji);
                          setShowEmojiPicker(false);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Icon name="MessageCircle" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Select a conversation</h3>
              <p className="text-muted-foreground mb-4">
                Choose a conversation from the sidebar or start a new one.
              </p>
              <Button variant="default" onClick={handleStartChat}>
                <Icon name="Plus" size={16} className="mr-2" />
                Start New Chat
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Start Chat Modal */}
      {showStartChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Start New Chat</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowStartChatModal(false);
                  setSelectedUser('');
                  setSelectedTeam('');
                  setError('');
                }}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Chat Type Selection - Only show for students */}
              {userProfile?.role === 'student' && (
                <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-4">
                 {/* <button
                    onClick={() => setChatType('user')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      chatType === 'user' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Direct Message
                  </button>
                  <button
                    onClick={() => setChatType('team')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      chatType === 'team' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    My Team
                  </button> */}
                </div>
              )}

              {/* User Selection */}
              {chatType === 'user' && (
                <div>
                  <Select
                    label="Select User to Chat With"
                    placeholder="Choose a user..."
                    searchable
                    loading={loadingUsers}
                    value={selectedUser}
                    onChange={setSelectedUser}
                    options={allUsers?.map(user => ({
                      value: user?.id,
                      label: `${user?.fullName} — ${user?.displayRole}`,
                      description: user?.role
                    }))}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    You can chat with any individual user from any role
                  </p>
                </div>
              )}

              {/* Team Selection - Only for students */}
              {chatType === 'team' && userProfile?.role === 'student' && (
                <div>
                  <Select
                    label="Select Your Team"
                    placeholder="Choose your team..."
                    searchable
                    loading={loadingTeams}
                    value={selectedTeam}
                    onChange={setSelectedTeam}
                    options={allTeams?.map(team => ({
                      value: team?.id,
                      label: team?.teamName,
                      description: `Team ${team?.teamNumber}`
                    }))}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Create a group chat with all members of your team
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowStartChatModal(false);
                    setSelectedUser('');
                    setSelectedTeam('');
                    setError('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={chatType === 'user' ? handleCreateUserConversation : handleCreateTeamConversation}
                  disabled={chatType === 'user' ? !selectedUser : !selectedTeam}
                >
                  Start Chat
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Group Chat Modal */}
      <NewTeamGroupChatModal
        isOpen={showGroupChatModal}
        onClose={() => setShowGroupChatModal(false)}
        onSuccess={handleGroupChatSuccess}
      />
    </div>
  );
};

export default TeamChat;